// src/pages/RenewalSummary.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./DaySummary.css";
import "./RenewalSummary.tweaks.css"; 

const API_BASE = "https://www.lianfoong.com/api/renewals/payments";
const PAGE_SIZE = 10; 

export default function RenewalSummary() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [summary, setSummary] = useState(null);
  const [details, setDetails] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("ALL"); 

  const [page, setPage] = useState(0);

  const businessDate = useMemo(() => {
    const stored = localStorage.getItem("customDate");
    return stored ? new Date(stored) : new Date();
  }, []);

  const yyyyMmDd = useMemo(() => {
    const d = businessDate;
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }, [businessDate]);

  const currency = (n) =>
    Number(n ?? 0).toLocaleString("en-SG", { style: "currency", currency: "SGD", minimumFractionDigits: 2 });

  const fetchData = async () => {
    setLoading(true);
    setErr("");
    try {
      const [s, d] = await Promise.all([
        fetch(`${API_BASE}/summary?date=${yyyyMmDd}`, { credentials: "include" }),
        fetch(
          `${API_BASE}/details?date=${yyyyMmDd}${sourceFilter === "ALL" ? "" : `&source=${sourceFilter}`}`,
          { credentials: "include" }
        ),
      ]);
      if (!s.ok) throw new Error(await s.text());
      if (!d.ok) throw new Error(await d.text());
      setSummary(await s.json());
      setDetails(await d.json());
    } catch (e) {
      setErr(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yyyyMmDd, sourceFilter]);

  useEffect(() => {
    setPage(0);
  }, [yyyyMmDd, sourceFilter, details?.rows?.length]);

  const allRows = details?.rows ?? [];
  const pageCount = Math.max(1, Math.ceil(allRows.length / PAGE_SIZE));

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") navigate("/dashboard");
      if (e.key === "ArrowRight") setPage((p) => Math.min(p + 1, Math.max(0, pageCount - 1)));
      if (e.key === "ArrowLeft") setPage((p) => Math.max(p - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, pageCount]); 

  const onDateChange = (e) => {
    const val = e.target.value;
    if (!val) return;
    localStorage.setItem("customDate", new Date(val).toISOString());
    window.location.reload();
  };
  
  const todayStr = new Date().toISOString().slice(0, 10);

  const downloadCsv = () => {
    const rows = details?.rows ?? [];
    const header = ["OldReceiptNo","NewReceiptNo","Principal","AmountPaid","Source","Time"];
    const lines = [header.join(",")];
    rows.forEach((r) => {
      lines.push(
        [r.oldReceiptNo ?? "", r.newReceiptNo ?? "", r.principalAmount ?? 0, r.paymentAmount ?? 0, r.source ?? "", r.time ?? ""]
          .map((x) => `"${String(x).replace(/"/g, '""')}"`)
          .join(",")
      );
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `renewal_details_${details?.date || yyyyMmDd}.csv`;
    document.body.appendChild(a); a.click();
    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 0);
  };

  // --- NEW: Handle Deletion ---
  const handleDelete = async (paymentId) => {
    if (!paymentId) return alert("Error: Missing Payment ID");

    const password = prompt("Enter password to delete this record:");
    if (password === null) return; // User clicked Cancel
    if (password !== "1234") {
      alert("Incorrect password.");
      return;
    }

    if (!window.confirm("Are you sure you want to PERMANENTLY delete this renewal payment record?")) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/${paymentId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (!res.ok) throw new Error(await res.text());
      
      alert("Record deleted successfully.");
      fetchData(); // Refresh the table automatically
    } catch (error) {
      alert("Failed to delete record: " + error.message);
    }
  };

  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * PAGE_SIZE;
  const end = Math.min(start + PAGE_SIZE, allRows.length);
  const pageRows = allRows.slice(start, end);

  if (loading) return <div className="ds-container"><div className="ds-card">Loading…</div></div>;
  if (err) return (
    <div className="ds-container">
      <div className="ds-card">
        <div className="ds-title">Renewal Summary</div>
        <div className="ds-error">Error: {err}</div>
        <button className="ds-btn" onClick={() => navigate("/dashboard")}>Back</button>
      </div>
    </div>
  );

  const canPrev = safePage > 0;
  const canNext = safePage < pageCount - 1;

  return (
    <div className="ds-renewal" style={{ paddingTop: 100 }}>
      <div className="ds-container">
        <div className="ds-card">
          <div className="ds-header">
            <div className="ds-title">Renewal Summary (In-Store vs Online)</div>
            <div className="ds-date-controls" style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <label className="ds-label">Date</label>
              <input type="date" defaultValue={yyyyMmDd} max={todayStr} onChange={onDateChange} />
              <div className="ds-date-chip">Showing: {summary?.date}</div>
            </div>
          </div>

          <div className="ds-panels">
            <div className="ds-panel">
              <div className="ds-panel-title">In-Store Renewals</div>
              <div className="ds-row">
                <div className="ds-label">Count</div>
                <div className="ds-value">{summary?.instoreCount ?? 0}</div>
                <div className="ds-amt">{currency(summary?.instoreTotal)}</div>
              </div>
            </div>
            <div className="ds-panel">
              <div className="ds-panel-title">Online Renewals</div>
              <div className="ds-row">
                <div className="ds-label">Count</div>
                <div className="ds-value">{summary?.onlineCount ?? 0}</div>
                <div className="ds-amt">{currency(summary?.onlineTotal)}</div>
              </div>
            </div>
          </div>

          <div className="ds-divider" />

          <div className="ds-footer">
            <div className="ds-footer-label">Total Payment Received</div>
            <div className="ds-footer-amt">{currency(summary?.grandTotal)}</div>
          </div>

          <div className="ds-divider" />

          <div className="ds-actions" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div className="ds-label">Filter</div>
            <select className="ds-input" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
              <option value="ALL">All Sources</option>
              <option value="ONLINE">Online Only</option>
              <option value="INSTORE">In-Store Only</option>
            </select>

            <div style={{ display: "flex", gap: 8, alignItems: "center", marginLeft: "auto" }}>
              <button className="ds-btn" onClick={() => setPage((p) => Math.max(p - 1, 0))} disabled={!canPrev}>
                ← Prev
              </button>
              <div style={{ minWidth: 140, textAlign: "center", opacity: 0.8 }}>
                Page {safePage + 1} / {pageCount}
              </div>
              <button className="ds-btn" onClick={() => setPage((p) => Math.min(p + 1, pageCount - 1))} disabled={!canNext}>
                Next →
              </button>
            </div>

            <button className="ds-btn" onClick={downloadCsv}>Download CSV</button>
            <button className="ds-btn" onClick={() => navigate("/dashboard")}>Back (Esc)</button>
          </div>

          <div className="ds-table-wrap" style={{ marginTop: 12 }}>
            <table className="ds-table">
              <thead>
                <tr>
                  <th>Old Receipt No</th>
                  <th>New Receipt No</th>
                  <th style={{ textAlign: "right" }}>Principal</th>
                  <th style={{ textAlign: "right" }}>Amount Paid</th>
                  <th>Source</th>
                  <th>Time</th>
                  <th style={{ textAlign: "center" }}>Action</th> {/* NEW HEADER */}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r, i) => (
                  <tr key={start + i}>
                    <td>{r.oldReceiptNo || ""}</td>
                    <td>{r.newReceiptNo || ""}</td>
                    <td style={{ textAlign: "right" }}>{currency(r.principalAmount)}</td>
                    <td style={{ textAlign: "right" }}>{currency(r.paymentAmount)}</td>
                    <td>{r.source}</td>
                    <td>{(r.time || "").slice(0, 8)}</td>
                    <td style={{ textAlign: "center" }}>
                      {/* NEW DELETE BUTTON */}
                      <button 
                        className="ds-btn" 
                        style={{ background: "#d32f2f", color: "white", padding: "4px 8px", fontSize: "12px", border: "none" }}
                        onClick={() => handleDelete(r.paymentId)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {allRows.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", opacity: 0.7 }}>
                      No payments found for this day / filter.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2} style={{ textAlign: "right", fontWeight: 600 }}>
                    Showing:
                  </td>
                  <td colSpan={1} style={{ textAlign: "left" }}>
                    {allRows.length === 0 ? "0 of 0" : `${start + 1}–${end} of ${allRows.length}`}
                  </td>
                  <td colSpan={2} style={{ textAlign: "right", fontWeight: 600 }}>Total</td>
                  <td colSpan={2} style={{ textAlign: "left", fontWeight: 600, paddingLeft: "15px" }}>{currency(details?.totalPayment)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

        </div>
      </div>
    </div>
  );
}